import * as React from 'react';
import { AriaAttributes, ForwardedRef } from 'react';

import cx from 'classnames';

type InputProps = JSX.IntrinsicElements['input'];

export interface Props extends AriaAttributes, InputProps {
  withBorder?: boolean;
  /** HTML ID for the element */
  id?: string;
  /** HTML name for the element */
  name?: string;
  /** onChange transparently passed to the input */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** Text to display when the input is empty */
  placeholder?: string;
  /** The default value for the field */
  defaultValue?: string;
  /** The current value for the field */
  value?: string;
  /** The (HTML) type for the input. */
  type?: string;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /** Native HTML pattern property, accepts a regular expression to validate the input */
  pattern?: string;
  ariaLabel?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  autoCorrect?: string;
  spellCheck?: boolean;
  className?: string;
  readOnly?: boolean;
}

const TextInput = React.forwardRef(
  (
    { className, disabled, withBorder = true, type = 'text', ...props }: Props,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const inputClass = cx(
      'w-full px-3 py-2 m-0 font-sans text-base leading-8 border-0 rounded-2 outline-none appearance-none focus:shadow-focus',
      {
        'shadow-border': withBorder,
        'bg-grey-50': disabled,
        [className]: !!className,
      }
    );

    return <input ref={ref} type={type} className={inputClass} {...props} />;
  }
);

TextInput.displayName = 'TextInput';

export default TextInput;
